<template>
  <div :style="{ backgroundImage: hero }" class="bg-cover bg-center relative h-screen sm:flex sm:items-center sm:justify-center">
    <div class="print:hidden absolute inset-0 h-full w-full bg-[var(--bookapp-hero-background-color)] opacity-[var(--bookapp-hero-background-opacity)]"  />
    <div v-if="giftCard"class="relative print:relative">
      <div class="min-h-screen sm:min-h-0 sm:max-h-[95vh] sm:overflow-y-auto p-5 text-secondary print:text-black sm:rounded text-center bg-secondary sm:max-w-lg mx-auto">
        <div
          v-if="$site.images && $site.images.logo"
          :style="{
            height: '100px',
            width: '80%',
            backgroundImage: 'url(' + $site.images.logo.medium + ')',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            margin: '0 auto 40px auto',
            borderRadius: '20px',
            '-webkit-print-color-adjust': 'exact !important',
            'color-adjust': 'exact !important'
          }"
        />
        <!--<img v-if="$site.images.logo" :src="$site.images.logo.medium" class="mb-5" style="height: 150px; max-width: 200px; border-radius: 20px">-->

        <div v-if="giftCard.gift && giftCard.gift.to && giftCard.gift.from" style="font-size:18px;line-height:145%;font-weight:normal" class="my-3">
          <p class="mb-2">
            {{ $t('pages.gift.card.preview.luckyYouName', { name: giftCard.gift.to.name }) }}
          </p>
          <p
            v-html="$t('pages.gift.card.claim.hasGivenYouAGift',{
              from: giftCard.gift.from.name,
              currency: $site.location.currencySymbol,
              balance: giftCard.balance || $site.location.currencySymbol,
              location: $site.location.title
            })"
          />
        </div>

        <div class="my-5 mx-auto shadow rounded relative max-w-[500px] min-h-[260px] max-h-[400px] overflow-hidden bg-action">
          <img :src="image" class="w-full h-full absolute inset-0 object-cover">

          <div v-if="!image" class="absolute flex items-center justify-center h-full w-full">
            <p class="text-action text-4xl font-bold">Gift card</p>
          </div>

          <div class="bg-primary p-4 absolute bottom-0 right-0 rounded-tl-lg">
            <p>
              <span class="text-primary text-xl font-bold">{{ giftCard.balanceLabel }}</span>
            </p>
          </div>
        </div>

        <div style="border-radius: 10px; padding: 20px;" class="my-5">
          <p style="font-size:18px;line-height:145%;font-weight:normal">
            {{ $t('pages.gift.card.preview.dearName', { name: giftCard.gift ? giftCard.gift.to.name : giftCard.customer.displayName }) }}
          </p>

          <p style="margin:12px 0;font-size:18px;line-height:145%;font-weight:normal">
            <template v-if="giftCard.gift && giftCard.gift.message">
              {{ giftCard.gift.message }}
            </template>
            <template v-else>
              {{ $t('pages.gift.card.preview.receivedGiftCard', { location: $site.location.title }) }}
            </template>
          </p>

          <p v-if="giftCard.gift && giftCard.gift.from" style="font-size:18px;opacity: 0.6" class="m-0">
            {{ $t('pages.gift.card.preview.from', { name: giftCard.gift.from.name }) }}
          </p>
        </div>

        <div v-if="$site.tabs.book" class="mb-4">
          <b-button :to="{ path: '/book-now', query: { giftcard: giftCard.displayNumber } }" variant="action" class="w-full print:hidden cursor-pointer">
            {{ $site.tabs.book.name }} »
          </b-button>

          <span class="book-now-link mx-auto " style="font-size: 26px">
            {{ $t('pages.gift.card.preview.visitToRedeem', { domain: domain }) }}
          </span>
        </div>

        <div class="mt-5" style="font-size:12px;">
          <div class="card-number">
            {{ $i18n.t('general.giftcardNumber', { number: giftCard.displayNumber }) }}
          </div>

          <div v-if="giftCard.expires">
            <strong>{{ $t('general.expires') }}:</strong> {{ giftCard.expiryDateLabel }}
          </div>

          <div v-if="giftCard.claimable" class="add-account-button mt-6 block">
            <b-button :to="{ path: '/gift/card/claim', query: $route.query }" variant="action" class="w-full cursor-pointer">
              {{ $t('general.addToMyAccount') }}
            </b-button>
          </div>

          <div v-if="giftCard.terms" style="font-size:11px; opacity: 0.7" class="mt-4">
            <p>{{ giftCard.terms.giftCardTermsTitle }}</p>
            <p>{{ giftCard.terms.giftCardTermsContent }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const route = useRoute()
const { $site, $config } = useNuxtApp()

definePageMeta({
  layout: 'full',
})

const { data } = useAsyncData(async () => {
  const route = useRoute()
  const { $api } = useNuxtApp()
  const query = route.query

  return await $api.giftcard.giftcard(query.id, query.signature)
})

const giftCard = computed(() => data.value?.giftCard)

const hero = computed(() => $site.images?.heroImageCssUrl('large'))

const image = computed(() => {
  if (giftCard.value && giftCard.value.images) {
    return giftCard.value.images.large
  } else if ($site.images.giftCard) {
    return $site.images.giftCard.large
  } else if ($site.images.hero) {
    return $site.images.hero.large
  }

  return null
})

const domain = computed(() => `${$site.slug}.${$config.domain}`)

onMounted(() => {
  if (route.query.print) {
    nextTick(() => {
      window.print()
    })
  }
})

const print = () => {
  nextTick(() => {
    window.print()
  })
}
</script>

<style>

.giftcard-print-preview {
  min-height: '100vh';
  width: '100vw'
}

.book-now-link {
  display: none
}

.book-now-button {
  display: block
}

@media print {
  .giftcard-print-preview {
    min-height: auto;
    width: auto
  }

  .book-now-link {
    display: block
  }

  .book-now-button {
    display: none
  }

  .add-account-button {
    display: none
  }

  .card-number {
    font-size: 22px !important
  }
}
</style>
